import NiceModal from '@ebay/nice-modal-react';
import {
	difference,
	flatten,
	forEachObjIndexed,
	has,
	includes,
	omit,
	prop,
	propOr,
	symmetricDifference,
	uniqBy,
} from 'ramda';
import { isNilOrEmpty } from 'ramda-extension';

import { resetForm, setValue } from '@gef-ui/form-core/stream/transfer/control';

import { EMPTY_VALUES } from 'constants/general';
import MODALS from 'constants/modals';
import dialog from 'features/common/dialog';
import fetch from 'features/common/fetch';
import { resetErrorsByPage } from 'features/common/submitFormDialog/slice';
import { searchUserTypes } from 'features/other/resolverSettings/constants';
import { getUserDataFromDBFlag } from 'features/other/resolverSettings/selectors';
import { setUserDataDBFlag } from 'features/other/resolverSettings/slice';
import userSearch from 'features/views/userSearch';

import { MARKET_PLACE_AGENCIES_SETTINGS_FORM_NAME } from '../form';
import {
	getAgencyManagersAndSubordinates,
	getAgencyManagersAndSubordinatesById,
	getManagerLogin,
	getMarketPlaceAgencies,
	getNotAssignedManagers,
	getOriginalValues,
	getPristineStates,
} from '../selectors';
import {
	resetAgencyManagersAndSubordinates,
	saveAgencies,
	saveAgencyManagersAndSubordinates,
	saveNotAssignedManagers,
	saveOriginalValues,
	savePristineStates,
	setManagerLogin,
} from '../slice';
import { dialogs } from './constants';
import { EDIT_AGENCY_MODAL_FORM_NAME } from './form';

export const startupEditAgencyModal =
	({ agency }) =>
	async (dispatch, getState, { getFormData }) => {
		// data for selected agency have been fetched and also might have been changed by user
		const agencyManagersAndSubordinates = getAgencyManagersAndSubordinates(getState());

		const agencyDataAlreadyExists = has(agency?.areaId, agencyManagersAndSubordinates);
		if (!agencyDataAlreadyExists) {
			const formData = await getFormData(MARKET_PLACE_AGENCIES_SETTINGS_FORM_NAME);

			dispatch(
				fetch.actions.get(fetch.constants.urls.settingsMarketPlaceAgencyManagersAndSubordinates, {
					replaceParams: { regionId: formData?.region, segmentId: formData?.segment, areaId: agency?.areaId },
					onSuccess: [
						(response) => () => {
							if (!response.body) return;
							const managersAndSubordinatesObj = {
								[agency?.areaId]: response?.body,
							};
							dispatch(saveOriginalValues({ agencyManagersAndSubordinates: managersAndSubordinatesObj }));
							dispatch(saveAgencyManagersAndSubordinates(managersAndSubordinatesObj));
						},
					],
				}),
			);
		} else {
			dispatch(saveOriginalValues({ agencyManagersAndSubordinates }));
		}
	};

export const saveCurrentAgencyChanges =
	({ agency }) =>
	(dispatch, getState) => {
		const pristineStates = getPristineStates(getState());
		const isAgencyPristine = prop(agency?.areaId, pristineStates);
		const { agencyManagersAndSubordinates } = getOriginalValues(getState());
		const agencyManagersAndSubordinatesByIdOriginal = propOr(
			EMPTY_VALUES.OBJECT,
			agency?.areaId,
		)(agencyManagersAndSubordinates);

		const agencyManagersAndSubordinatesByIdCurrent = getAgencyManagersAndSubordinatesById(
			getState(),
			agency?.areaId,
		);

		const isDifferent = !isNilOrEmpty(
			symmetricDifference(agencyManagersAndSubordinatesByIdCurrent, agencyManagersAndSubordinatesByIdOriginal),
		);

		if (isDifferent) {
			const updatedAgencyManagers = agencyManagersAndSubordinatesByIdCurrent.map((manager) => {
				const managerInArea = manager?.subordinates.find(
					(subordinate) => manager?.userLogin === subordinate?.userLogin,
				)?.assigned;

				// manager is included in subordinates array - so we need to filter manager out
				const realSubordinates = manager?.subordinates.filter(
					(subordinate) => subordinate?.userLogin !== manager?.userLogin,
				);
				const subordinatesCount = realSubordinates?.length;
				const subordinatesInArea = realSubordinates.filter((subordinate) => subordinate?.assigned);
				const subordinatesInAreaCount = subordinatesInArea?.length;

				return {
					...manager,
					managerInArea,
					subordinatesCount,
					subordinatesInAreaCount,
				};
			});

			// update for agencyOverview
			dispatch(updateAgenciesAfterSaving({ updatedAgencyManagers, agency }));

			// possible update for edit agency modal overview
			// when user unassigns everyone and hits "continue" - dont show the manager on next modal open
			dispatch(updateAgencyManagersAndSubordinatesAfterSaving({ updatedAgencyManagers, agency }));

			dispatch(updateNotAssignedManagersAfterSaving({ updatedAgencyManagers, agency }));

			if (isAgencyPristine) {
				dispatch(savePristineStates({ [agency?.areaId]: false }));
			}
		} else {
			// returning empty state, because there have not been any changes yet - agency is still pristine
			if (isAgencyPristine) {
				const agencyManagersAndSubordinates = getAgencyManagersAndSubordinates(getState());
				const updated = omit([agency?.areaId], agencyManagersAndSubordinates);
				dispatch(resetAgencyManagersAndSubordinates());
				dispatch(saveAgencyManagersAndSubordinates(updated));
			}
		}

		// reseting original agencyManagers so we can set new state on next modal mount
		dispatch(saveOriginalValues({ agencyManagersAndSubordinates: EMPTY_VALUES.OBJECT }));
		NiceModal.hide(MODALS.agency_detail);
	};

export const updateAgenciesAfterSaving =
	({ updatedAgencyManagers, agency }) =>
	(dispatch, getState) => {
		const managersAssignedInSomeWay = updatedAgencyManagers.filter(
			(manager) => manager?.managerInArea || manager?.subordinatesInAreaCount > 0,
		);

		const agencies = getMarketPlaceAgencies(getState());
		const updatedAgencies = agencies.map((area) => {
			if (area?.areaId === agency?.areaId) {
				return {
					...area,
					assignedManagers: managersAssignedInSomeWay,
				};
			} else {
				return area;
			}
		});

		dispatch(saveAgencies(updatedAgencies));
	};

export const updateAgencyManagersAndSubordinatesAfterSaving =
	({ updatedAgencyManagers, agency }) =>
	(dispatch, getState) => {
		// filter out managers that are not assigned in the area and dont have any assigned subordinates too

		const agencyManagersAndSubordinatesById = getAgencyManagersAndSubordinatesById(getState(), agency?.areaId);

		const managersNotAssignedInAnyWay = updatedAgencyManagers.filter(
			(manager) => !manager?.managerInArea && manager?.subordinatesInAreaCount === 0,
		);
		const managersNotAssignedInAnyWayLogins = managersNotAssignedInAnyWay.map((manager) => manager?.userLogin);

		// if such manager exists update agencyManagersAndSubordinates for edit agency modal
		if (!isNilOrEmpty(managersNotAssignedInAnyWayLogins)) {
			const update = agencyManagersAndSubordinatesById.filter(
				(manager) => !includes(manager?.userLogin, managersNotAssignedInAnyWayLogins),
			);

			const managersAndSubordinatesObj = {
				[agency?.areaId]: update,
			};
			dispatch(saveAgencyManagersAndSubordinates(managersAndSubordinatesObj));
		}
	};

export const updateNotAssignedManagersAfterSaving =
	({ updatedAgencyManagers, agency }) =>
	(dispatch, getState) => {
		const { agencyManagersAndSubordinates } = getOriginalValues(getState());
		const agencyManagersAndSubordinatesByIdOriginal = propOr(
			EMPTY_VALUES.OBJECT,
			agency?.areaId,
		)(agencyManagersAndSubordinates);

		const agencyManagersAndSubordinatesByIdCurrent = getAgencyManagersAndSubordinatesById(
			getState(),
			agency?.areaId,
		);

		const deletedWholeManagers = difference(
			agencyManagersAndSubordinatesByIdOriginal,
			agencyManagersAndSubordinatesByIdCurrent,
		);

		const deletedLogins = deletedWholeManagers.map((deleted) => deleted?.userLogin);
		const agencies = getMarketPlaceAgencies(getState());
		const allAssignedManagers = flatten(agencies.map((agency) => agency?.assignedManagers));

		// checking if "deleted" managers are listed in some other agencies
		// we are adding to notAssignedManagers only the managers that are not listed anywhere
		const loginsToAddToNotAssignedManagers = deletedLogins.map((login) => {
			const isAssignedElseWhere = allAssignedManagers.find((manager) => manager?.userLogin === login);
			return isAssignedElseWhere ? null : login;
		});

		// to be deleted = to be added to notAssign
		const cleanedLoginsToAdd = loginsToAddToNotAssignedManagers.filter((login) => login);
		const deletedFiltered = deletedWholeManagers.filter((manager) =>
			includes(manager?.userLogin, cleanedLoginsToAdd),
		);

		const deletedFormatted = deletedFiltered.map((manager) => {
			const managerInArea = manager?.subordinates.find(
				(subordinate) => manager?.userLogin === subordinate?.userLogin,
			)?.assigned;

			// manager is included in subordinates array - so we need to filter manager out
			const realSubordinates = manager?.subordinates.filter(
				(subordinate) => subordinate?.userLogin !== manager?.userLogin,
			);

			return {
				...manager,
				managerInArea,
				subordinatesCount: realSubordinates?.length,
			};
		});

		// adding "to be deleted" managers into original data
		const notAssignedManagers = getNotAssignedManagers(getState());
		const notAssignedManagersAndToBeDeletedManagers = [...notAssignedManagers, ...deletedFormatted];
		const uniqueNotAssignedManagers = uniqBy(
			(notAssigned) => notAssigned?.userLogin,
			notAssignedManagersAndToBeDeletedManagers,
		);

		const managersAssignedInSomeWay = updatedAgencyManagers.filter(
			(manager) => manager?.managerInArea || manager?.subordinatesInAreaCount > 0,
		);
		const assignedAgencyManagersLogins = managersAssignedInSomeWay.map((assigned) => assigned?.userLogin);

		// additionally filtering out the newly assigned managers
		const filter = uniqueNotAssignedManagers.filter(
			(notAssigned) => !includes(notAssigned?.userLogin, assignedAgencyManagersLogins),
		);

		dispatch(saveNotAssignedManagers(filter));
	};

export const toggleSubordinateAssignStatusBasedOnAgencyAndManager =
	(agencyId, managerLogin, subordinateUserLogin) => (dispatch, getState) => {
		const agencyManagersAndSubordinatesById = getAgencyManagersAndSubordinatesById(getState(), agencyId);
		const updatedAgencyManagersAndSubordinates = agencyManagersAndSubordinatesById.map((manager) => {
			if (manager?.userLogin === managerLogin) {
				return {
					...manager,
					subordinates: manager?.subordinates.map((subordinate) => {
						if (subordinate?.userLogin === subordinateUserLogin) {
							return { ...subordinate, assigned: !subordinate?.assigned };
						} else {
							return subordinate;
						}
					}),
				};
			} else {
				return manager;
			}
		});

		dispatch(saveAgencyManagersAndSubordinates({ [agencyId]: updatedAgencyManagersAndSubordinates }));
	};

export const tryAddingManager =
	({ agency }) =>
	async (dispatch, getState, { sendControlNext, getFormData }) => {
		const formData = await getFormData(EDIT_AGENCY_MODAL_FORM_NAME);
		const userDatafromDB = getUserDataFromDBFlag(getState());
		const savedManagerLogin = getManagerLogin(getState());

		// close and reset all the "required" errors
		dispatch(dialog.actions.close(EDIT_AGENCY_MODAL_FORM_NAME));
		dispatch(resetErrorsByPage({ page: EDIT_AGENCY_MODAL_FORM_NAME }));

		if (!userDatafromDB) {
			const result = await dispatch(
				userSearch.actions.searchUser({
					type: searchUserTypes.MARKET_PLACE_AGENCIES_SEARCH,
					formData: {
						login: formData?.login,
						firstName: formData?.userFirstName,
						surname: formData?.userSurname,
						sellerIn: formData?.sellerIn,
					},
				}),
			);

			if (result && result?.body.length === 1) {
				const { userLogin, sellerIn, firstName, surname, managerLogin } = result?.body[0];
				dispatch(setManagerLogin(managerLogin));

				const fieldsToUpdateResolverSettings = {
					login: userLogin,
					sellerIn,
					userFirstName: firstName,
					userSurname: surname,
				};

				forEachObjIndexed((value, key) => {
					sendControlNext(EDIT_AGENCY_MODAL_FORM_NAME, setValue({ fieldName: key, value }));
				}, fieldsToUpdateResolverSettings);

				dispatch(dialog.actions.close(dialogs.NON_UNIQUE_OR_NON_EXISTANT_USER));
				dispatch(
					fetchAgencyManager({
						agency,
						managerLogin,
						userLogin,
						withSubordinates: formData?.withSubordinates,
					}),
				);
			} else {
				dispatch(dialog.actions.open(dialogs.NON_UNIQUE_OR_NON_EXISTANT_USER, null, dialog.constants.WARNING));
				return false;
			}
		} else {
			// user has been already searched and not changed, no need to searchUser again
			dispatch(dialog.actions.close(dialogs.NON_UNIQUE_OR_NON_EXISTANT_USER));
			dispatch(
				fetchAgencyManager({
					agency,
					managerLogin: savedManagerLogin,
					userLogin: formData?.login,
					withSubordinates: formData?.withSubordinates,
				}),
			);
		}
	};

export const fetchAgencyManager =
	({ agency, managerLogin, userLogin, withSubordinates }) =>
	async (dispatch, getState, { getFormData }) => {
		const formDataMarketPlaceAgencies = await getFormData(MARKET_PLACE_AGENCIES_SETTINGS_FORM_NAME);

		dispatch(
			fetch.actions.get(fetch.constants.urls.settingsMarketPlaceAgencyManagersAndSubordinates, {
				replaceParams: {
					regionId: formDataMarketPlaceAgencies?.region,
					segmentId: formDataMarketPlaceAgencies?.segment,
					areaId: agency?.areaId,
				},
				queryParams: { managerLogin },
				onSuccess: [
					(response) => () => {
						if (!response.body) return;

						// successfull response returns only one manager
						const [manager] = response.body;

						dispatch(
							addNewManager({
								agency: agency?.areaId,
								manager,
								userLogin,
								withSubordinates,
							}),
						);
					},
				],
			}),
		);
	};

export const addNewManager =
	({ agency, manager, userLogin, withSubordinates }) =>
	(dispatch, getState, { sendControlNext }) => {
		const agencyManagersAndSubordinates = getAgencyManagersAndSubordinatesById(getState(), agency);
		const isManager = userLogin === manager?.userLogin;
		const managerAlreadyAdded = agencyManagersAndSubordinates.find(
			(oldManager) => oldManager?.userLogin === manager?.userLogin,
		);

		let updatedAgencyManagersAndSubordinates;

		// completely new manager
		if (!managerAlreadyAdded) {
			let newManager;

			if (isManager) {
				if (withSubordinates) {
					newManager = {
						...manager,
						subordinates: manager?.subordinates.map((subordinate) => ({ ...subordinate, assigned: true })),
					};
				}

				if (!withSubordinates) {
					newManager = {
						...manager,
						subordinates: manager?.subordinates.map((subordinate) => {
							if (subordinate?.userLogin === manager?.userLogin) {
								return { ...subordinate, assigned: true };
							} else {
								return subordinate;
							}
						}),
					};
				}
			} else {
				// temporary for non managers (sellers etc) - missing withSubordinates value in BE
				newManager = {
					...manager,
					subordinates: manager?.subordinates.map((subordinate) => {
						if (subordinate?.userLogin === userLogin) {
							return { ...subordinate, assigned: true };
						} else {
							return subordinate;
						}
					}),
				};
			}

			updatedAgencyManagersAndSubordinates = [newManager, ...agencyManagersAndSubordinates];
		} else {
			// manager that has been already added in history
			// add "assigned" subordinates from new fetch to current user's state

			updatedAgencyManagersAndSubordinates = agencyManagersAndSubordinates.map((oldManager) => {
				let updatedSubordinates;

				if (isManager) {
					if (oldManager?.userLogin === manager?.userLogin) {
						if (withSubordinates) {
							updatedSubordinates = oldManager?.subordinates.map((subordinate) => {
								return {
									...subordinate,
									assigned: true,
								};
							});
						} else {
							updatedSubordinates = oldManager?.subordinates.map((subordinate) => {
								if (subordinate?.userLogin === manager?.userLogin) {
									return {
										...subordinate,
										assigned: true,
									};
								} else {
									return subordinate;
								}
							});
						}

						return {
							...oldManager,
							subordinates: updatedSubordinates,
						};
					} else {
						return oldManager;
					}
				} else {
					// non manager
					if (oldManager?.userLogin === manager?.userLogin) {
						updatedSubordinates = oldManager?.subordinates.map((subordinate) => {
							if (subordinate?.userLogin === userLogin) {
								return {
									...subordinate,
									assigned: true,
								};
							} else {
								return subordinate;
							}
						});

						return {
							...oldManager,
							subordinates: updatedSubordinates,
						};
					} else {
						return oldManager;
					}
				}
			});
		}

		const managersAndSubordinatesObj = {
			[agency]: updatedAgencyManagersAndSubordinates,
		};
		dispatch(saveAgencyManagersAndSubordinates(managersAndSubordinatesObj));
		dispatch(setUserDataDBFlag(false));
		dispatch(setManagerLogin(null));
		sendControlNext(EDIT_AGENCY_MODAL_FORM_NAME, resetForm());
	};

export const deleteWholeManager =
	({ agency, manager }) =>
	(dispatch, getState) => {
		const agencyManagersAndSubordinates = getAgencyManagersAndSubordinatesById(getState(), agency?.areaId);

		const updatedAgencyManagersAndSubordinates = agencyManagersAndSubordinates.filter(
			(oldManager) => oldManager?.userLogin !== manager?.userLogin,
		);
		const managersAndSubordinatesObj = {
			[agency?.areaId]: updatedAgencyManagersAndSubordinates,
		};
		dispatch(saveAgencyManagersAndSubordinates(managersAndSubordinatesObj));
	};
